<template>
  <div 
    v-if="shouldShow"
    :class="containerClasses"
    role="complementary"
    aria-label="Advertisement space"
  >
    <div class="ad-placeholder-content">
      <div class="text-center p-4">
        <div class="text-gray-400 dark:text-gray-500 mb-2">
          <svg class="w-8 h-8 mx-auto" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
          </svg>
        </div>
        <p class="text-xs text-gray-500 dark:text-gray-400">
          {{ placeholderText }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  adType: 'banner' | 'rectangle' | 'square' | 'infeed';
  show?: boolean;
  className?: string;
}

const props = withDefaults(defineProps<Props>(), {
  show: true,
  className: ''
});

const shouldShow = computed(() => props.show);

const containerClasses = computed(() => [
  'ad-placeholder',
  props.className,
  {
    'ad-banner-placeholder': props.adType === 'banner',
    'ad-rectangle-placeholder': props.adType === 'rectangle',
    'ad-square-placeholder': props.adType === 'square',
    'ad-infeed-placeholder': props.adType === 'infeed'
  }
]);

const placeholderText = computed(() => {
  switch (props.adType) {
    case 'banner':
      return 'Banner Advertisement Space';
    case 'rectangle':
      return 'Advertisement';
    case 'square':
      return 'Advertisement';
    case 'infeed':
      return 'Sponsored Content';
    default:
      return 'Advertisement';
  }
});
</script>

<style scoped>
.ad-placeholder {
  @apply bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg;
  @apply transition-all duration-300;
}

.ad-banner-placeholder {
  @apply w-full;
  min-height: 90px;
}

.ad-rectangle-placeholder {
  @apply w-80 mx-auto;
  height: 250px;
}

.ad-square-placeholder {
  @apply w-64 mx-auto;
  height: 250px;
}

.ad-infeed-placeholder {
  @apply w-full;
  min-height: 120px;
}

.ad-placeholder-content {
  @apply h-full flex items-center justify-center;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .ad-rectangle-placeholder,
  .ad-square-placeholder {
    @apply w-full max-w-sm;
  }
}
</style>